import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(new Audio());
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [playlist, setPlaylist] = useState([]);
  const [playlistIndex, setPlaylistIndex] = useState(-1);
  const [queue, setQueue] = useState([]);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('off'); // 'off' | 'all' | 'one'

  useEffect(() => {
    const audio = audioRef.current;
    audio.volume = volume;
    
    const onTimeUpdate = () => setProgress(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onEnded = () => {
      // Repeat one: replay the same track
      if (repeat === 'one') {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
        return;
      }
      // Play from queue first
      if (queue.length > 0) {
        const [nextFromQueue, ...restQueue] = queue;
        setQueue(restQueue);
        setCurrentTrack(nextFromQueue);
        audioRef.current.src = `${API}/stream/${nextFromQueue.id}`;
        audioRef.current.play().catch(() => {});
        return;
      }
      if (playlistIndex < playlist.length - 1) {
        const nextIdx = shuffle
          ? Math.floor(Math.random() * playlist.length)
          : playlistIndex + 1;
        playTrackAtIndexInternal(nextIdx);
      } else if (repeat === 'all' && playlist.length > 0) {
        // Loop back to beginning
        playTrackAtIndexInternal(0);
      } else {
        setIsPlaying(false);
      }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [playlistIndex, playlist.length, queue, shuffle, repeat]);

  const playTrackAtIndexInternal = (index) => {
    if (index < 0 || index >= playlist.length) return;
    const track = playlist[index];
    setPlaylistIndex(index);
    setCurrentTrack(track);
    audioRef.current.src = `${API}/stream/${track.id}`;
    audioRef.current.play().catch(() => {});
  };

  const playTrackAtIndex = useCallback((index) => {
    playTrackAtIndexInternal(index);
  }, [playlist]);

  const playTrack = useCallback((track, trackList = []) => {
    if (trackList.length > 0) {
      setPlaylist(trackList);
      const idx = trackList.findIndex(t => t.id === track.id);
      setPlaylistIndex(idx >= 0 ? idx : 0);
    }
    setCurrentTrack(track);
    audioRef.current.src = `${API}/stream/${track.id}`;
    audioRef.current.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
  }, [isPlaying]);

  const seekTo = useCallback((time) => {
    audioRef.current.currentTime = time;
  }, []);

  const changeVolume = useCallback((v) => {
    setVolume(v);
    audioRef.current.volume = v;
  }, []);

  const nextTrack = useCallback(() => {
    if (queue.length > 0) {
      const [nextFromQueue, ...restQueue] = queue;
      setQueue(restQueue);
      setCurrentTrack(nextFromQueue);
      audioRef.current.src = `${API}/stream/${nextFromQueue.id}`;
      audioRef.current.play().catch(() => {});
      return;
    }
    if (playlistIndex < playlist.length - 1) {
      playTrackAtIndex(shuffle ? Math.floor(Math.random() * playlist.length) : playlistIndex + 1);
    }
  }, [playlistIndex, playlist.length, playTrackAtIndex, queue, shuffle]);

  const prevTrack = useCallback(() => {
    if (progress > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    if (playlistIndex > 0) {
      playTrackAtIndex(playlistIndex - 1);
    }
  }, [playlistIndex, playTrackAtIndex, progress]);

  const addToQueue = useCallback((track) => {
    setQueue(prev => [...prev, track]);
  }, []);

  const removeFromQueue = useCallback((index) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
  }, []);

  const reorderQueue = useCallback((fromIndex, toIndex) => {
    setQueue(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffle(prev => !prev);
  }, []);

  const toggleRepeat = useCallback(() => {
    setRepeat(prev => prev === 'off' ? 'all' : prev === 'all' ? 'one' : 'off');
  }, []);

  const [radioMode, setRadioMode] = useState(false);
  const [radioShowName, setRadioShowName] = useState(null);
  const [radioMeta, setRadioMeta] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [streamQuality, setStreamQuality] = useState('fast'); // 'fast' | 'slow' | 'offline'
  const [usingLowStream, setUsingLowStream] = useState(false);
  const streamsRef = useRef({ primary: process.env.REACT_APP_DEFAULT_STREAM_URL || 'https://c14.radioboss.fm:8037/stream', low: '' });
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const radioModeRef = useRef(false);
  useEffect(() => { radioModeRef.current = radioMode; }, [radioMode]);
  const RECONNECT_STEPS_MS = [1500, 3000, 6000, 12000, 20000];

  const classifyConnection = useCallback(() => {
    if (typeof navigator === 'undefined') return 'fast';
    if (navigator.onLine === false) return 'offline';
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return 'fast';
    const eff = c.effectiveType || '';
    if (eff === 'slow-2g' || eff === '2g' || eff === '3g') return 'slow';
    if (c.saveData === true) return 'slow';
    return 'fast';
  }, []);

  const pickStreamUrl = useCallback(() => {
    const { primary, low } = streamsRef.current;
    const q = classifyConnection();
    const wantLow = q === 'slow' && !!low;
    setUsingLowStream(wantLow);
    return wantLow ? low : primary;
  }, [classifyConnection]);

  const clearReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    setIsReconnecting(false);
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!radioModeRef.current) return;
    if (reconnectTimeoutRef.current) return;
    const idx = Math.min(reconnectAttemptRef.current, RECONNECT_STEPS_MS.length - 1);
    const delay = RECONNECT_STEPS_MS[idx];
    reconnectAttemptRef.current += 1;
    setIsReconnecting(true);
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      try {
        const url = pickStreamUrl();
        // Force a fresh connection: reset src so browser reopens the socket
        audioRef.current.src = url;
        audioRef.current.load();
        audioRef.current.play().catch(() => scheduleReconnect());
      } catch {
        scheduleReconnect();
      }
    }, delay);
  }, [pickStreamUrl]);

  // Fetch stream URLs once
  useEffect(() => {
    axios.get(`${API}/settings/streams`).then(r => {
      const primary = (r.data?.primary_url || streamsRef.current.primary).trim();
      const low = (r.data?.low_url || '').trim();
      streamsRef.current = { primary, low };
    }).catch(() => { /* keep defaults */ });
  }, []);

  // Wire audio error + network listeners for auto-reconnect
  useEffect(() => {
    const audio = audioRef.current;
    const onError = () => {
      if (radioModeRef.current) scheduleReconnect();
    };
    const onStalled = () => {
      if (radioModeRef.current && !audio.paused) scheduleReconnect();
    };
    const onPlaying = () => {
      // Successful reconnection
      clearReconnect();
    };
    const onOnline = () => {
      setStreamQuality(classifyConnection());
      if (radioModeRef.current && audioRef.current.paused) {
        reconnectAttemptRef.current = 0;
        clearReconnect();
        scheduleReconnect();
      }
    };
    const onOffline = () => {
      setStreamQuality('offline');
      if (radioModeRef.current) setIsReconnecting(true);
    };
    const onConnectionChange = () => setStreamQuality(classifyConnection());

    audio.addEventListener('error', onError);
    audio.addEventListener('stalled', onStalled);
    audio.addEventListener('playing', onPlaying);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    conn?.addEventListener?.('change', onConnectionChange);
    setStreamQuality(classifyConnection());
    return () => {
      audio.removeEventListener('error', onError);
      audio.removeEventListener('stalled', onStalled);
      audio.removeEventListener('playing', onPlaying);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      conn?.removeEventListener?.('change', onConnectionChange);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [classifyConnection, scheduleReconnect, clearReconnect]);

  // If quality flips while in radio mode and helps (fast<->slow), swap stream URL
  useEffect(() => {
    if (!radioModeRef.current) return;
    const url = pickStreamUrl();
    if (audioRef.current.src !== url && url) {
      audioRef.current.src = url;
      audioRef.current.load();
      audioRef.current.play().catch(() => scheduleReconnect());
    }
  }, [streamQuality, pickStreamUrl, scheduleReconnect]);

  // Poll RadioBOSS metadata when in radio mode
  useEffect(() => {
    if (!radioMode) { setRadioMeta(null); return; }
    const fetchMeta = () => {
      axios.get(`${API}/radio/nowplaying`).then(r => {
        setRadioMeta(r.data);
        if (r.data.artist || r.data.title) {
          setCurrentTrack(prev => prev?.id === '__live__' ? {
            ...prev,
            title: r.data.title || 'Live Stream',
            artist: r.data.artist || 'Christovibes Radio',
          } : prev);
        }
      }).catch(() => {});
    };
    fetchMeta();
    const interval = setInterval(fetchMeta, 8000);
    return () => clearInterval(interval);
  }, [radioMode]);

  const startRadio = useCallback(() => {
    setRadioMode(true);
    radioModeRef.current = true;
    setRadioShowName('Christovibes Radio');
    setCurrentTrack({ id: '__live__', title: 'Live Stream', artist: 'Christovibes Radio' });
    setPlaylist([]);
    setPlaylistIndex(-1);
    clearReconnect();
    audioRef.current.src = pickStreamUrl();
    audioRef.current.play().catch(() => scheduleReconnect());
  }, [pickStreamUrl, scheduleReconnect, clearReconnect]);

  return (
    <PlayerContext.Provider value={{
      currentTrack, isPlaying, progress, duration, volume, playlist, queue, shuffle, repeat,
      playTrack, togglePlay, seekTo, changeVolume, nextTrack, prevTrack,
      addToQueue, removeFromQueue, reorderQueue, clearQueue, toggleShuffle, toggleRepeat,
      playlistIndex, startRadio, radioMode, radioShowName, radioMeta,
      isReconnecting, streamQuality, usingLowStream,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
}
